import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Eye, FileText, FileSpreadsheet, Printer } from 'lucide-react';
import { PreviewGaps } from './PreviewGaps';
import { PreviewFeedback, CommentBox } from './PreviewFeedback';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const plain = (s) => (s || '').replace(/<[^>]+>/g, '').trim();

/** Same numbering rule as the Word export: hand-set number wins; 1 / 1.1 / 1.1.1 only when the group auto-numbers. */
export const sectionNumbers = (secs, numbering) => {
  const auto = numbering === 'auto'; const c = [0, 0, 0, 0];
  return secs.map((s) => {
    const lvl = Math.min(4, Math.max(1, Number(s.level || 1)));
    c[lvl - 1] += 1; for (let k = lvl; k < 4; k += 1) c[k] = 0;
    return s.number || (auto ? c.slice(0, lvl).filter(Boolean).join('.') : '');
  });
};

const Md = ({ children, className = '' }) => (
  <div className={`rfp-md ${className}`}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ''}</ReactMarkdown>
  </div>
);

/** In-browser rendition of every document the client will receive — narratives laid out like the Word file, sheets like the Excel/Word matrix.
 * Reads straight from the project so it updates as you edit. `readOnly` = public preview link (no gaps panel, no "seen" tracking). */
export default function PreviewPanel({ project, onSeen, onJump, readOnly = false, assignees, onAssign, comments, onComment, commentToken }) {
  const narr = (project.deliverables || []).filter((d) => ['narrative', 'generated'].includes(d.type));
  const sheets = (project.documents || []).filter((d) => Array.isArray(d.questions));
  const items = useMemo(() => [...narr.map((d) => ({ kind: 'narrative', id: d.id, title: d.title, d })), ...sheets.map((d) => ({ kind: 'sheet', id: d.id, title: d.filename, d }))], [narr, sheets]);
  const [sel, setSel] = useState(items[0]?.id);
  const [fbKey, setFbKey] = useState(0);
  const threads = useMemo(() => {
    const m = {};
    (comments || []).forEach((c) => { const t = c.target || {}; const k = t.kind === 'section' ? `s:${t.sid}` : t.kind === 'question' ? `q:${t.doc_id}:${t.idx}` : ''; if (k) (m[k] = m[k] || []).push(c); });
    return m;
  }, [comments]);
  const commentProps = commentToken ? { token: commentToken, threads, onPosted: onComment } : null;
  useEffect(() => { if (!items.find((x) => x.id === sel)) setSel(items[0]?.id); }, [items, sel]);
  useEffect(() => {
    if (!items.length || readOnly) return;
    axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/preview-seen`, {}, { withCredentials: true }).then((r) => onSeen?.(r.data.preview_seen_at)).catch(() => {});
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = items.find((x) => x.id === sel);
  if (!items.length) return <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md p-6 text-center" data-testid="rfp-preview-empty">{readOnly ? 'Nothing has been built for this response yet.' : 'Nothing to preview yet — build the response from the Container tab first.'}</p>;
  const jump = (it) => {
    if (it.kind === 'section') setSel(it.did); else setSel(it.docId);
    onJump?.(it);
  };
  return (
    <div className={`grid grid-cols-1 gap-4 ${readOnly ? 'lg:grid-cols-[240px_1fr]' : 'lg:grid-cols-[220px_1fr_260px]'}`} data-testid="rfp-preview-panel">
      <aside className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 px-1 flex items-center gap-1"><Eye className="w-3 h-3" />What the client receives</div>
        {items.map((it, i) => (
          <button key={it.id} type="button" onClick={() => setSel(it.id)} className={`w-full text-left rounded-md px-2.5 py-1.5 text-xs flex items-center gap-2 ${sel === it.id ? 'bg-[#0F2040] text-white' : 'hover:bg-slate-100 text-slate-700'}`} data-testid={`rfp-preview-item-${i}`}>
            {it.kind === 'narrative' ? <FileText className="w-3.5 h-3.5 shrink-0" /> : <FileSpreadsheet className="w-3.5 h-3.5 shrink-0" />}
            <span className="truncate flex-1">{it.title}</span>
            <span className={`text-[9px] ${sel === it.id ? 'text-white/70' : 'text-slate-400'}`}>{it.kind === 'narrative' ? `${(it.d.sections || []).length} sec` : `${it.d.questions.length} rows`}</span>
          </button>
        ))}
        <p className="text-[10px] text-slate-400 px-1 pt-2">{readOnly ? (commentToken ? 'Read-only view — use "comment" on any section or row to send feedback to the team.' : 'Read-only view.') : 'Live view of the current text — edit on the Narratives or Annexures tab and come back.'} <button type="button" className="underline hover:text-[#0F2040] inline-flex items-center gap-0.5" onClick={() => window.print()} data-testid="rfp-preview-print"><Printer className="w-3 h-3" />print</button></p>
      </aside>
      <section className="bg-slate-100 rounded-lg p-4 overflow-auto max-h-[75vh]" data-testid="rfp-preview-page">
        {cur?.kind === 'narrative' ? <NarrativePage project={project} d={cur.d} cp={commentProps} /> : cur ? <SheetPage d={cur.d} cp={commentProps} /> : null}
      </section>
      {!readOnly && (
        <div className="space-y-3 lg:max-h-[75vh] overflow-auto">
          <PreviewFeedback project={project} onJump={jump} refreshKey={fbKey} />
          <PreviewGaps project={project} onJump={jump} assignees={assignees} onAssign={onAssign ? async (it, who) => { await onAssign(it, who); setFbKey((k) => k + 1); } : undefined} />
        </div>
      )}
    </div>
  );
}

const NarrativePage = ({ project, d, cp }) => {
  const secs = d.sections || [];
  const nums = sectionNumbers(secs, d.numbering);
  const H = { 1: 'text-xl', 2: 'text-lg', 3: 'text-base', 4: 'text-sm' };
  return (
    <article className="bg-white shadow-md mx-auto max-w-[800px] px-14 py-12 min-h-[900px] font-serif text-[13px] leading-relaxed text-slate-800" data-testid="rfp-preview-narrative">
      <h1 className="text-2xl font-bold text-[#0F2040] font-sans">{d.title}</h1>
      <p className="text-xs text-slate-500 mb-8 font-sans">{[project.client_name, project.name].filter(Boolean).join(' · ')}</p>
      {secs.length === 0 && <p className="text-slate-400 italic">[No sections yet]</p>}
      {secs.map((s, i) => (
        <div key={s.id || i} className="mb-5" style={{ marginLeft: `${(Math.min(4, Number(s.level || 1)) - 1) * 12}px` }} data-testid={`rfp-preview-section-${i}`}>
          <h2 className={`${H[Math.min(4, Number(s.level || 1))]} font-semibold text-[#0F2040] font-sans mb-1`}>{nums[i] ? `${nums[i]} ` : ''}{s.heading}
            {cp && <CommentBox token={cp.token} thread={cp.threads[`s:${s.id}`] || []} onPosted={cp.onPosted} target={{ kind: 'section', did: d.id, sid: s.id, label: `${nums[i] ? `${nums[i]} ` : ''}${s.heading}` }} testId={`rfp-preview-comment-s-${i}`} />}
          </h2>
          {plain(s.draft) ? <Md>{s.draft}</Md> : <p className="text-slate-400 italic">[Not yet drafted]</p>}
        </div>
      ))}
    </article>
  );
};

const SheetPage = ({ d, cp }) => (
  <div className="bg-white shadow-md mx-auto max-w-[1100px] p-6 min-h-[600px]" data-testid="rfp-preview-sheet">
    <h1 className="text-lg font-bold text-[#0F2040] mb-3">{d.filename}</h1>
    <table className="w-full text-xs border-collapse">
      <thead><tr className="bg-[#0F2040] text-white text-left"><th className="border border-slate-300 px-2 py-1 w-16">No.</th><th className="border border-slate-300 px-2 py-1 w-[38%]">Requirement</th><th className="border border-slate-300 px-2 py-1 w-24">Response</th><th className="border border-slate-300 px-2 py-1">Supplier response</th></tr></thead>
      <tbody>
        {(d.questions || []).map((q, i) => {
          const prev = i > 0 ? d.questions[i - 1] : null;
          return (
            <React.Fragment key={q.idx}>
              {q.section && q.section !== prev?.section && <tr><td colSpan={4} className="border border-slate-300 bg-slate-100 px-2 py-1 font-semibold text-[#0F2040]">{q.section}</td></tr>}
              <tr className="align-top" data-testid={`rfp-preview-row-${i}`}>
                <td className="border border-slate-300 px-2 py-1 font-mono text-slate-500">{q.no || i + 1}</td>
                <td className="border border-slate-300 px-2 py-1 text-slate-800">{q.text}</td>
                <td className="border border-slate-300 px-2 py-1 text-slate-700">{q.client_score || ''}</td>
                <td className="border border-slate-300 px-2 py-1">{q.is_pricing ? <span className="text-slate-500">PRICING — see Cost Summary</span> : plain(q.response) ? <Md>{q.response}</Md> : <span className="text-slate-400 italic">[Response to be drafted]</span>}
                  {cp && <CommentBox token={cp.token} thread={cp.threads[`q:${d.id}:${q.idx}`] || []} onPosted={cp.onPosted} target={{ kind: 'question', doc_id: d.id, idx: q.idx, label: `${q.no || i + 1}. ${q.text}` }} testId={`rfp-preview-comment-q-${i}`} />}
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);
